/**
 * @file MaintenanceContext
 * @description Platform maintenance state for the whole app.
 *
 *   level  none     nothing shown
 *          banner   a notice across the top; everything works
 *          feature  a notice; AI features are refused by the server
 *          full     every page is replaced by the maintenance page, except the
 *                   sign-in and admin areas so staff can get in and clear it
 *
 *   Polled gently and paused while the tab is hidden. A failed poll says
 *   nothing about maintenance (it is the visitor's connection, or the whole
 *   platform being down — which the static page at the proxy handles), so it
 *   leaves the last state alone. Any refused call can ask for an immediate
 *   re-check through MAINTENANCE_CHECK_EVENT.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchMaintenance, MAINTENANCE_CHECK_EVENT } from '../api/maintenance.js';
import { Button } from '../components/elements/Button.jsx';

const POLL_MS = 30_000;
const POLL_ACTIVE_MS = 10_000;

const QUIET = { level: 'none', message: null, cause: null, source: null, since: null, expectedBackAt: null };
const Ctx = createContext({ ...QUIET, recheck: () => {} });

export const useMaintenance = () => useContext(Ctx);

/** Staff and sign-in keep working under full maintenance. */
export const staysOpenUnderFull = (pathname = '') =>
  /^\/(admin|login)(\/|$)/.test(pathname);

export function MaintenanceProvider({ children }) {
  const [state, setState] = useState(QUIET);

  const recheck = useCallback(async () => {
    try {
      const next = await fetchMaintenance();
      setState({ ...QUIET, ...(next || {}) });
    } catch (_) { /* leave the last known state */ }
  }, []);

  useEffect(() => {
    let timer;
    let stopped = false;
    const loop = async () => {
      if (stopped) return;
      if (typeof document === 'undefined' || !document.hidden) await recheck();
      if (!stopped) timer = setTimeout(loop, state.level === 'none' ? POLL_MS : POLL_ACTIVE_MS);
    };
    loop();
    const onCheck = () => { recheck(); };
    window.addEventListener(MAINTENANCE_CHECK_EVENT, onCheck);
    return () => { stopped = true; clearTimeout(timer); window.removeEventListener(MAINTENANCE_CHECK_EVENT, onCheck); };
  }, [recheck, state.level]);

  const value = useMemo(() => ({ ...state, recheck }), [state, recheck]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

// A return time that has passed is not a promise any more: say it is taking
// longer rather than keep announcing a time that is gone.
export const backText = (iso, now = Date.now()) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  if (d.getTime() <= now) return 'This is taking longer than we expected. Thank you for your patience.';
  return `We expect to be back around ${d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}.`;
};

/** How long a maintenance state has been on, in whole minutes (0 when unknown). */
export const minutesOn = (sinceIso, now = Date.now()) => {
  if (!sinceIso) return 0;
  const t = new Date(sinceIso).getTime();
  return Number.isFinite(t) ? Math.max(0, Math.floor((now - t) / 60000)) : 0;
};

/** "2 hours 5 minutes" — for the staff banner and the admin card. */
export const durationText = (minutes) => {
  const h = Math.floor(minutes / 60); const m = minutes % 60;
  return [h ? `${h} hour${h === 1 ? '' : 's'}` : null, m || !h ? `${m} minute${m === 1 ? '' : 's'}` : null].filter(Boolean).join(' ');
};

// Past this, maintenance someone set is probably forgotten.
export const LONG_RUNNING_MINUTES = 60;

export function MaintenanceBanner() {
  const { level, message, expectedBackAt, source, since } = useMaintenance();
  if (level !== 'banner' && level !== 'feature' && !(level === 'full' && staysOpenUnderFull(window.location.pathname))) return null;
  const longOn = source === 'manual' && minutesOn(since) >= LONG_RUNNING_MINUTES;
  const staffNote = level === 'full'
    ? `Maintenance mode is ON${longOn ? ` (for ${durationText(minutesOn(since))})` : ''} — customers see the maintenance page. `
    : '';
  return (
    <div className="w-full border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-sm text-amber-900" role="status" data-testid="maintenance-banner">
      {staffNote}{message}{backText(expectedBackAt) ? ` ${backText(expectedBackAt)}` : ''}
    </div>
  );
}

export function MaintenancePage() {
  const { message, expectedBackAt, recheck } = useMaintenance();
  return (
    <main className="flex min-h-screen items-center justify-center bg-background px-6" role="alert" data-testid="maintenance-page">
      <div className="max-w-lg text-center">
        <h1 className="text-2xl font-semibold text-neutral">We&apos;ll be right back</h1>
        <p className="mt-4 text-base leading-7 text-gray-700">{message}</p>
        {backText(expectedBackAt) ? <p className="mt-2 text-sm text-gray-500">{backText(expectedBackAt)}</p> : null}
        <div className="mt-6"><Button variant="outline" onClick={recheck}>Check again</Button></div>
      </div>
    </main>
  );
}

/** Wraps the app: the banner above it, or the maintenance page instead of it. */
export function MaintenanceLayer({ children }) {
  const { level } = useMaintenance();
  if (level === 'full' && !staysOpenUnderFull(window.location.pathname)) return <MaintenancePage />;
  return (<><MaintenanceBanner />{children}</>);
}
